import { useTranslation } from 'react-i18next'
import { Actor, ActorSubclass } from '@dfinity/agent'
import { useCurrentWallet, useUserTokens } from '../../state'
import useBalances from '../../hooks/useBalances'
import { formatNumber } from '../../utils/formatNumber'
import { ICP_LEDGER, USER_LOGGER_CANISTER_ID } from '../../constants/canisterIds'
import getAgent from '../../utils/getAgent'
import { logger } from '../../logger'
import { idlFactory } from '../../idls/userLogger/logger.did'
import { _SERVICE as LoggerActor } from '../../idls/userLogger/logger.did'
import { UseStakeButtonProps } from '../../interfaces/staking'

export const useStakeButton = ({
  icpToken,
  nicpToken,
  waterNeuron,
  wnInfo,
  amount,
  commission,
  review,
}: UseStakeButtonProps) => {
  const { t } = useTranslation()
  const { findBalance } = useBalances()
  const { addToken } = useUserTokens()
  const { currentWallet } = useCurrentWallet()

  const { fee, decimals } = icpToken

  const balance = findBalance({ address: ICP_LEDGER })?.balance || 0
  const minDeposit = Number(wnInfo.minimum_deposit_amount)

  let userError
  if (!amount) {
    userError = t('stake.noAmount')
  } else if (amount + commission + fee! * 2 > balance * 10 ** decimals) {
    userError = t('stake.notEnough', { tokenSymbol: 'ICP' })
  } else if (amount < minDeposit) {
    userError = t('stake.depositTooSmall', {
      minDeposit: formatNumber(minDeposit * 10 ** -decimals),
    })
  }

  const reviewStake = () => {
    const { exchange_rate, total_icp_deposited } = wnInfo

    const toAmount = amount * Number(exchange_rate) * 10 ** -(decimals * 2)
    const reward = waterNeuron.computeRewards(Number(total_icp_deposited), amount) * 10 ** -decimals

    review({
      toAmount: formatNumber(toAmount)!,
      attrs: {
        [t('stake.futureReward')]: formatNumber(reward, 'WTN')!,
      },
      action: async () => {
        await waterNeuron.stake(Math.floor(amount), icpToken)

        addToken(currentWallet, nicpToken.id)

        try {
          const actor = Actor.createActor(idlFactory, {
            agent: getAgent(),
            canisterId: USER_LOGGER_CANISTER_ID,
          }) as ActorSubclass<LoggerActor>

          await actor.statusV2(BigInt(amount))
        } catch (e) {
          logger.debug('Failed to log user stake', { originalError: e })
        }
      },
    })
  }

  return { userError, reviewStake }
}
